import React, { useState, useCallback } from 'react';
import { Button, Popover, theme } from '@app/ui';
import {
    CloseOutlined,
    ReadOutlined,
    FileSearchOutlined,
    CloudOutlined,
    ExportOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import {
    SUPPORT_CENTER_URL,
    METHODOLOGY_OVERVIEW_URL,
    API_DOCUMENTATION_URL,
} from './constants';
import { HelpIcon } from './HelpIcon';

import './ResourceCenter.scss';

type ResourceItem = {
    key: string;
    icon: React.ReactNode;
    titleKey: string;
    descriptionKey: string;
    url: string;
};

const ResourceCenter = () => {
    const { t } = useTranslation('common');
    const {
        token: { colorPrimary, colorText, colorTextSecondary },
    } = theme.useToken();
    const dividerColor = 'rgba(0, 0, 0, 0.06)';
    const [open, setOpen] = useState(false);

    const iconStyle = { color: colorPrimary, fontSize: 20 };

    const items: ResourceItem[] = [
        {
            key: 'platformIntel',
            icon: <ReadOutlined style={iconStyle} />,
            titleKey: 'resourceCenter.platformIntel.title',
            descriptionKey: 'resourceCenter.platformIntel.description',
            url: SUPPORT_CENTER_URL,
        },
        {
            key: 'mediaValuation101',
            icon: <FileSearchOutlined style={iconStyle} />,
            titleKey: 'resourceCenter.mediaValuation101.title',
            descriptionKey: 'resourceCenter.mediaValuation101.description',
            url: METHODOLOGY_OVERVIEW_URL,
        },
        {
            key: 'apiIntegration',
            icon: <CloudOutlined style={iconStyle} />,
            titleKey: 'resourceCenter.apiIntegration.title',
            descriptionKey: 'resourceCenter.apiIntegration.description',
            url: API_DOCUMENTATION_URL,
        },
    ];

    const handleItemClick = useCallback(
        (url: string) => {
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
            setOpen(false);
        },
        [],
    );

    const content = (
        <div className="resource-center-popover">
            <div className="resource-center-popover__header">
                <div className="resource-center-popover__header-left">
                    <span className="resource-center-popover__logo">
                        <span className="resource-center-popover__logo-text">
                            relo
                        </span>
                    </span>
                    <span
                        className="resource-center-popover__title"
                        style={{ color: colorText }}
                    >
                        {t('resourceCenter.title')}
                    </span>
                </div>
                <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setOpen(false)}
                    className="resource-center-popover__close"
                    aria-label={t('resourceCenter.closeAriaLabel')}
                />
            </div>
            <div
                className="resource-center-popover__divider"
                style={{ borderColor: dividerColor }}
            />
            <div className="resource-center-popover__list">
                {items.map((item, index) => (
                    <React.Fragment key={item.key}>
                        <button
                            type="button"
                            className="resource-center-popover__item"
                            onClick={() => handleItemClick(item.url)}
                            disabled={!item.url}
                        >
                            <span className="resource-center-popover__item-icon">
                                {item.icon}
                            </span>
                            <span className="resource-center-popover__item-text">
                                <span
                                    className="resource-center-popover__item-title"
                                    style={{ color: colorText }}
                                >
                                    {t(item.titleKey)}
                                </span>
                                <span
                                    className="resource-center-popover__item-description"
                                    style={{ color: colorTextSecondary }}
                                >
                                    {t(item.descriptionKey)}
                                </span>
                            </span>
                            <ExportOutlined
                                className="resource-center-popover__item-external"
                                style={{ color: colorTextSecondary }}
                            />
                        </button>
                        {index < items.length - 1 && (
                            <div
                                className="resource-center-popover__item-divider"
                                style={{ borderColor: dividerColor }}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            placement="bottomRight"
            open={open}
            onOpenChange={setOpen}
            overlayClassName="resource-center-popover-overlay"
        >
            <Button
                type="text"
                icon={<HelpIcon size={32} />}
                onClick={() => setOpen(true)}
                className="resource-center-trigger"
                data-test="resource-center-trigger"
                aria-label={t('resourceCenter.triggerAriaLabel')}
            />
        </Popover>
    );
};

export default ResourceCenter;
